import { Sparkles, Star, Smartphone, Zap } from "lucide-react";
import QuoteForm from "./QuoteForm";
import { MEDIA } from "../../lib/site";

export default function HeroQuote() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 sm:pt-32 pb-12 sm:pb-16 overflow-hidden"
        >
            {/* Background */}
            <div className="absolute inset-0 -z-10">
                <div
                    className="absolute inset-0 opacity-25"
                    style={{
                        backgroundImage: `url(${MEDIA.heroBg})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/80 to-[#050505]" />
                <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] rounded-full bg-blue-600/15 blur-[120px]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-blue-500/10 blur-[100px]" />
                <div className="grain" />
            </div>

            <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                {/* Left: brand copy */}
                <div className="lg:col-span-6 text-left">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
                        <Sparkles className="w-3.5 h-3.5 text-blue-400" strokeWidth={1.8} />
                        <span className="text-xs text-zinc-300 tracking-wide">One tap. Endless engagement.</span>
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02]"
                    >
                        Tap. <span className="text-zinc-500">Connect.</span>
                        <br />
                        <span className="bg-gradient-to-r from-white via-white to-blue-300 bg-clip-text text-transparent">
                            Grow.
                        </span>
                    </h1>

                    <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl">
                        Smart NFC plaques that turn a single phone tap into a Google review, menu view, or social follow &mdash; no app, no friction.
                    </p>

                    {/* Quick stats / proof */}
                    <div className="mt-7 grid grid-cols-3 gap-4 max-w-md">
                        <Stat icon={Star} value="5★" label="Reviews lift" />
                        <Stat icon={Smartphone} value="No app" label="Tap & go" />
                        <Stat icon={Zap} value="<1s" label="Open speed" />
                    </div>

                    {/* Social proof */}
                    <div className="mt-8 flex items-center gap-4">
                        <div className="flex -space-x-2">
                            {[1, 2, 3, 4].map((i) => (
                                <div
                                    key={i}
                                    className="w-7 h-7 rounded-full border-2 border-[#050505] bg-gradient-to-br from-blue-500/40 to-blue-700/40"
                                />
                            ))}
                        </div>
                        <div>
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                ))}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">
                                Trusted by 200+ local businesses
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: quote form */}
                <div className="lg:col-span-6">
                    <QuoteForm compact />
                </div>
            </div>
        </section>
    );
}

function Stat({ icon: Icon, value, label }) {
    return (
        <div className="glass rounded-xl p-3">
            <Icon className="w-4 h-4 text-blue-400 mb-1.5" strokeWidth={1.6} />
            <div className="font-display text-lg leading-none">{value}</div>
            <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">{label}</div>
        </div>
    );
}
